'use client'

import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../../../lib/cn'

export interface GlowTopProps {
  /**
   * Color source from theme variables
   * @default 'primary'
   */
  color?: 'primary' | 'accent' | 'muted'

  /**
   * Enable "light turning on" entrance animation
   * @default true
   */
  animate?: boolean

  /**
   * Additional CSS classes
   */
  className?: string
}

/**
 * GlowTop - Radial gradient glow emanating from top center
 *
 * Creates a soft, theme-aware gradient glow effect that works in both
 * light and dark modes. Uses OKLCH relative color syntax for theme integration.
 *
 * Fixed intensity: Light 0.25, Dark 0.15
 *
 * @example
 * ```tsx
 * // Static
 * <GlowTop />
 *
 * // With entrance animation
 * <GlowTop animate />
 * ```
 */
export function GlowTop({
  color = 'primary',
  animate = true,
  className,
}: GlowTopProps) {
  const prefersReducedMotion = useReducedMotion()
  const colorVar = color === 'muted' ? '--muted-foreground' : `--${color}`

  const lightStyle = {
    background: `radial-gradient(
      ellipse 80% 60% at 50% 0%,
      oklch(from var(${colorVar}) calc(l - 0.1) c h / 0.25),
      transparent 70%
    )`,
  }

  const darkStyle = {
    background: `radial-gradient(
      ellipse 80% 60% at 50% 0%,
      oklch(from var(${colorVar}) calc(l - 0.1) c h / 0.15),
      transparent 70%
    )`,
  }

  // Static version
  if (!animate) {
    return (
      <>
        <div
          className={cn('absolute inset-0 z-0 dark:hidden', className)}
          style={lightStyle}
        />
        <div
          className={cn('absolute inset-0 z-0 hidden dark:block', className)}
          style={darkStyle}
        />
      </>
    )
  }

  // Animated version
  return (
    <>
      <motion.div
        className={cn('absolute inset-0 z-0 origin-top dark:hidden', className)}
        style={lightStyle}
        initial={{ opacity: 0, scaleY: 0.3, scaleX: 0.6 }}
        animate={{ opacity: 1, scaleY: 1, scaleX: 1 }}
        transition={{
          duration: prefersReducedMotion ? 0.3 : 1,
          ease: [0.16, 1, 0.3, 1],
        }}
      />
      <motion.div
        className={cn('absolute inset-0 z-0 origin-top hidden dark:block', className)}
        style={darkStyle}
        initial={{ opacity: 0, scaleY: 0.3, scaleX: 0.6 }}
        animate={{ opacity: 1, scaleY: 1, scaleX: 1 }}
        transition={{
          duration: prefersReducedMotion ? 0.3 : 1,
          ease: [0.16, 1, 0.3, 1],
        }}
      />
    </>
  )
}
